// AudiologyScreeningPage.tsx
import React, { useMemo, useState, useEffect, JSX } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../../components/Center Admin/layout/AppLayout";
import "../../center admin css/Nurse2Page.css";
import "../../center admin css/centerAdmin.css";

import AudiologyActivePatientCard from "../../components/Center Admin/Reuse/AudiologyActivePatientCard";

// Forms (same set your Nurse1 page uses)
import { AnthropometricMeasurementForm } from "../../components/Center Admin/forms/AnthropometricMeasurementForm";
import { PhysicalExaminationForm } from "../../components/Center Admin/forms/PhysicalExaminationForm";
import { ADMinorSymptomsExaminationForm } from "../../components/Center Admin/forms/ADMinorSymptomsExaminationForm";
import { BUSampleForm } from "../../components/Center Admin/forms/BUSampleForm";
import BnuSampleForm from "../../components/Center Admin/forms/sentToLab";
import LabReportForm from "../../components/Center Admin/forms/LabReportForm";
import { VisionScreeningForm } from "../../components/Center Admin/forms/VisionScreeningForm";
import { OralImagingForm } from "../../components/Center Admin/forms/OralImagingForm";
import { VitalsExaminationForm } from "../../components/Center Admin/forms/VitalsExaminationForm";
import { SalivaSampleForm } from "../../components/Center Admin/forms/SalivaSampleForm";
import { CariogramInputForm } from "../../components/Center Admin/forms/CariogramInputForm";
import AudiologyScreeningForm from "../../components/Center Admin/forms/AudiologyScreeningForm";

/** ===== Helpers & Types (same as Nurse1) ===== */
const normalize = (s?: string) =>
  (s ?? "").toString().trim().toUpperCase().replace(/\s+/g, " ");

type BackendForm = {
  _id?: string;
  name?: string;
  key?: string;
  category?: string;
  isActive?: boolean;
};

type AllowedForm = {
  id: string;
  label: string;
  render: (emrId: string) => JSX.Element;
};

/** 🔁 Robust user loader: check multiple keys in session & local storage */
function loadUserFromStorage(): any | null {
  const keys = [
    "currentUser",
    "CurrrentResponse",
    "CurrentResponse",
    "backendResponse",
  ];
  const stores: Storage[] = [sessionStorage, localStorage];

  const read = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  for (const s of stores) {
    for (const k of keys) {
      const v = read(s, k);
      if (v && typeof v === "object") return v;
    }
  }
  return null;
}

/** ===== AudiologyScreeningPage (dynamic like Nurse1) ===== */
const AudiologyScreeningPage: React.FC = () => {
  // mirror Nurse1’s toggle (in case you later add a queue view here)
  const [activeView] = useState<"forms" | "queue">("forms");

  // Active patient selection comes from AppLayout via Outlet context
  // const { activePatient } = useOutletContext<OutletCtx>();

  // const { activePatient } = useOutletContext<any>();

  // const emrId = activePatient?.child?.childId || "";

  const { activePatient } = useOutletContext<any>();

  const emrId = activePatient?.child?.childId || "";

  useEffect(() => {
    setFormStatus({
      audiology: false,
    });

    if (!activePatient) {
      setIsStarted(false);
    }
  }, [activePatient]);

  // dynamic forms state
  const [loadingForms, setLoadingForms] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visibleForms, setVisibleForms] = useState<AllowedForm[]>([]);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [formStatus, setFormStatus] = useState<{
    audiology: boolean;
  }>({
    audiology: false,
  });

  const [isStarted, setIsStarted] = useState(false);

  const updateFormStatus = (key: keyof typeof formStatus, value: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateBeforeComplete = () => {
    if (!formStatus.audiology) {
      alert("Please complete Audiology Screening form");
      return false;
    }
    return true;
  };

  const FORM_COMPONENTS: Record<string, (emrId: string) => JSX.Element> = {
    ANTHROPOMETRY: (emrId) => <AnthropometricMeasurementForm emrId={emrId} />,
    "ANTHROPOMETRIC MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "ANTHROPOMETRY MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "PHYSICAL EXAMINATION": (emrId) => (
      <PhysicalExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYSPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "AD MINOR SYMPTOMS": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "BLOOD AND URIN SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm emrId={emrId} />
    ),
    "BLOOD AND URINE SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm emrId={emrId} />
    ),
    "BLOOD SAMPLE": (emrId) => <BUSampleForm emrId={emrId} />,
    "BLOOD SAMPLE SENT TO LAB": (emrId) => <BnuSampleForm emrId={emrId} />,
    "LAB REPORT": (emrId) => <LabReportForm emrId={emrId} />,
    "VISION SCREENING": (emrId) => <VisionScreeningForm emrId={emrId} />,
    "ORAL IMAGING": (emrId) => <OralImagingForm emrId={emrId} />,
    "VITALS EXAMINATION": (emrId) => <VitalsExaminationForm emrId={emrId} />,
    "CARIOGRAM SALAIVA SAMPLE": (emrId) => <SalivaSampleForm emrId={emrId} />,
    "CARIOGRAM INPUT": (emrId) => <CariogramInputForm emrId={emrId} />,

    "AUDIOLOGY SCREENING": (emrId) => (
      <AudiologyScreeningForm
        emrId={emrId}
        onComplete={() => updateFormStatus("audiology", true)}
      />
    ),
  };

  useEffect(() => {
    const handler = () => {
      setIsStarted(true);
    };

    window.addEventListener("patientUpdated", handler);

    return () => {
      window.removeEventListener("patientUpdated", handler);
    };
  }, []);

  useEffect(() => {
    setLoadingForms(true);
    setLoadError(null);

    try {
      const user = loadUserFromStorage();
      if (!user) {
        throw new Error("User data not found in session. Please log in again.");
      }

      // prefer role-based forms, fallback to user.forms (same as Nurse1)
      const fromRole: BackendForm[] =
        user?.roleAssignments?.[0]?.roleId?.formIds ?? [];
      const fallback: BackendForm[] = user?.forms ?? [];

      const backendForms: BackendForm[] =
        (fromRole?.length ? fromRole : fallback)?.filter(
          (f) => f?.isActive !== false,
        ) ?? [];

      const built: AllowedForm[] = [];
      const seen = new Set<string>();

      backendForms.forEach((bf) => {
        const nameN = normalize(bf?.name);
        const keyN = normalize(bf?.key);
        const renderer =
          FORM_COMPONENTS[nameN] ?? FORM_COMPONENTS[keyN] ?? null;
        if (!renderer) return;

        const label = bf?.name || bf?.key || "Form";
        const unique = bf?._id || `${label}-${keyN || nameN}`;
        if (!seen.has(unique)) {
          seen.add(unique);
          built.push({ id: unique, label, render: renderer });
        }
      });

      setVisibleForms(built);
      setActiveIdx((prev) => (built[prev] ? prev : 0));

      if (built.length === 0) {
        setTimeout(() => {
          window.alert(
            "No form is assigned to your role. Please contact the Super Admin.",
          );
        }, 0);
      }
    } catch (err: any) {
      console.error("AudiologyScreeningPage: failed to load user forms", err);
      setLoadError(err.message || "Could not load permitted forms.");
      setVisibleForms([]);
      setActiveIdx(0);
    } finally {
      setLoadingForms(false);
    }
  }, []);

  return (
    <section className="bnu-main">
      <AudiologyActivePatientCard
        onPatientSelect={(patient) => console.log("Selected:", patient)}
        showActions={true}
        validateBeforeComplete={validateBeforeComplete}
        onStationStarted={() => setIsStarted(true)}
      />

      {activeView === "forms" && (
        <>
          <div className="nurse-subtabs">
            {loadingForms && (
              <div className="nurse-subtab disabled">Loading…</div>
            )}

            {!loadingForms && loadError && (
              <div className="nurse-subtab disabled">{loadError}</div>
            )}

            {!loadingForms && !loadError && visibleForms.length > 0 && (
              <>
                {visibleForms.map((f, i) => (
                  <button
                    key={f.id}
                    className={`nurse-subtab${
                      activeIdx === i ? " active" : ""
                    }`}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    title={f.label}
                  >
                    {f.label}
                  </button>
                ))}
              </>
            )}

            {!loadingForms && !loadError && visibleForms.length === 0 && (
              <div className="nurse-subtab disabled">No forms assigned</div>
            )}
          </div>

          {isStarted &&
            !loadingForms &&
            !loadError &&
            visibleForms[activeIdx] && (
              <>{visibleForms[activeIdx].render(emrId)}</>
            )}
        </>
      )}
    </section>
  );
};

export default AudiologyScreeningPage;
